import { useFieldArray, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSet } from 'react-use';
import { dataToForm, FormValues } from './ExpensesDetailed.meta';
import { useSaveExpensesMutation } from '../../api/useSaveExpensesMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { showOPLConfirmationDialog } from '../../../../helpers/showOPLConfirmationDialog';
import { useEstimateExpensesMutation } from '../../api/useEstimateExpensesMutation';
import { MESSAGES } from '../../../../CommonConstants';
import {
  ExpenseCategory,
  ExpenseFrequency,
  ExpensesDTO,
  ExpensesMode,
} from '../../api/useExpensesQuery';
import { useEstimateExpenseCategoryMutation } from '../../api/useEstimateExpenseCategoryMutation';

interface Params {
  clientId: string;
  data: ExpensesDTO;
  workflowProps?: WorkflowComponentProps;
}

export const useExpensesDetailed = ({ clientId, data, workflowProps }: Params) => {
  const formBag = useForm<FormValues>({
    defaultValues: dataToForm(data),
  });

  const expensesFieldArray = useFieldArray({
    control: formBag.control,
    name: 'expenses',
    keyName: 'key',
  });
  const watchFieldArray = formBag.watch('expenses');
  const controlledFields = expensesFieldArray.fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const saveExpensesMutation = useSaveExpensesMutation({ clientId });

  const estimateExpensesMutation = useEstimateExpensesMutation();

  const estimateExpenseCategoryMutation = useEstimateExpenseCategoryMutation();

  const [, categoryEstimatingSetActions] = useSet<ExpenseCategory>();

  const [, recurrenceSetActions] = useSet(
    new Set(
      data.DetailedExpenses.reduce((acc, item) => {
        if (item.RepeatEveryNYears !== null && item.RepeatForNYears !== null) {
          acc.push(item.Id);
        }

        return acc;
      }, [] as string[]),
    ),
  );

  useEffect(() => {
    if (isEmpty(formBag.formState.dirtyFields)) {
      ADV.util.Router.unregisterNavigateAwayCallback();
    } else {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    }
  }, [formBag.formState]);

  const handleEstimateCategory = (category: ExpenseCategory, estimatedValue: number) => {
    const index = controlledFields.findIndex(field => field.Name === category);

    if (index !== -1) {
      recurrenceSetActions.remove(controlledFields[index].Id);
      formBag.setValue(`expenses.${index}.Amount`, estimatedValue);
      formBag.setValue(`expenses.${index}.Frequency`, ExpenseFrequency.Monthly);
      formBag.setValue(`expenses.${index}.Year`, null);
      formBag.setValue(`expenses.${index}.RepeatEveryNYears`, null);
      formBag.setValue(`expenses.${index}.RepeatForNYears`, null);
    }
  };

  const estimateExpenses = async () => {
    try {
      const { EstimatedValues } = await estimateExpensesMutation.mutateAsync({ clientId });

      EstimatedValues.forEach(({ EstimatedValue, Category }) => {
        handleEstimateCategory(Category, EstimatedValue);
      });
    } catch (error) {
      console.error(error);
      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  const estimateExpenseCategory = async (category: ExpenseCategory) => {
    try {
      categoryEstimatingSetActions.add(category);

      const { EstimatedValue } = await estimateExpenseCategoryMutation.mutateAsync({
        clientId,
        category,
      });

      handleEstimateCategory(category, EstimatedValue);
    } catch (error) {
      console.error(error);
      toast.error(MESSAGES.GENERIC_ERROR);
    } finally {
      categoryEstimatingSetActions.remove(category);
    }
  };

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      await saveExpensesMutation.mutateAsync({
        clientId,
        expensesInputMode: ExpensesMode.Detailed,
        detailedExpenses: values.expenses,
      });

      formBag.reset(values);

      if (workflowProps) {
        workflowProps.onNext();
      } else {
        toast.success('Your expenses were successfully saved.');
      }
    } catch (error) {
      console.error(error);
    }
  });

  const recurrenceYearsOptions = data.YearsOptions.reduce(
    (acc, year) => {
      acc.push({
        label: year.toString(),
        value: year,
      });

      return acc;
    },
    [{ label: 'Starts today', value: null } as { label: string; value: number | null }],
  );

  return {
    formBag,
    expensesFieldArray: {
      ...expensesFieldArray,
      fields: controlledFields,
    },
    recurrenceYearsOptions,
    onSubmit,
    saveExpensesMutation,
    estimateExpensesMutation,
    estimateExpenses,
    estimateExpenseCategory,
    categoryEstimatingSetActions,
    recurrenceSetActions,
  };
};
